import { z } from 'zod';

/** RFQ lifecycle, from the purchaser's send to a terminal outcome. */
export const RFQ_STATUSES = [
  'matching',
  'matched',
  'quoted',
  'accepted',
  'rejected',
  'expired',
] as const;

export type RfqStatus = (typeof RFQ_STATUSES)[number];

export const rfqStatusSchema = z.enum(RFQ_STATUSES);

/** Statuses past which a supplier pairing no longer accepts new messages. */
export const TERMINAL_RFQ_STATUSES: readonly RfqStatus[] = ['accepted', 'rejected', 'expired'];

/** An `Order` row is created in the same transaction that accepts a quote. */
export function hasRfqOrder(status: RfqStatus): boolean {
  return status === 'accepted';
}

/**
 * Counterparty organization name, masked until the RFQ has an order.
 * @param status Status of the supplier pairing.
 * @param name Real organization name, when the relation was loaded.
 * @param placeholder Generic label shown while the counterparty stays hidden.
 * @returns The real name once an order exists, the placeholder while masked, or null when no name is known.
 */
export function maskedOrgName(
  status: RfqStatus,
  name: string | null | undefined,
  placeholder: string,
): string | null {
  if (!name) return null;
  return hasRfqOrder(status) ? name : placeholder;
}

/** Masked label for the purchaser side of an RFQ, shown to the supplier. */
export const PURCHASER_LABEL = 'Anonymous Purchaser';

/**
 * Masked label for a supplier, numbered by its position on the RFQ.
 * @param index Zero-based position in the RFQ's supplier list.
 * @returns A label such as "Supplier 1".
 */
export function supplierLabel(index: number): string {
  return `Supplier ${index + 1}`;
}

/**
 * Initials of a person's name, for masked chat senders.
 * @param name Full name, or null when unknown.
 * @returns Uppercase initials such as "JD", or null when no name is known.
 */
export function initialsOf(name: string | null | undefined): string | null {
  if (!name) return null;
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/**
 * Quote lifecycle.
 *
 * - `pending`   — draft, possibly unpriced, supplier-only visibility
 * - `quoted`    — sent to the purchaser; sets `quoted_on`
 * - `declined`  — purchaser rejected it, directly or as a sibling of an accepted quote
 * - `withdrawn` — supplier retracted a *sent* quote; drafts are deleted instead
 */
export const QUOTE_STATUSES = [
  'pending',
  'quoted',
  'accepted',
  'declined',
  'expired',
  'withdrawn',
] as const;

export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const quoteStatusSchema = z.enum(QUOTE_STATUSES);
